import React, { useState, useEffect } from 'react';
import Logo from '../components/common/Logo';
import Sidebar from '../components/dashboard-patient/PatientSidebar';
import { Calendar, User, Euro, X, MessageSquare, Clock, Video } from 'lucide-react';
import AppointmentService from '../services/appoinment.service';
import { useNavigate } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import LeaveFeedbackForm from '../components/LeaveFeedbackForm';
import { UPLOAD_PREFIX } from '../config/appConfig';
import { getStatusLabel, getStatusColor } from '../utils/appointmentUtils';

const PatientBookingHistoryPage = () => {
  const navigate = useNavigate();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    loadAppointments();
  }, []);
  
  const loadAppointments = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('auth_token');
      console.log('Token prima della richiesta:', token);
      
      console.log('Sto per chiamare getPatientAppointments()');
      const response = await AppointmentService.getPatientAppointments();
      
      // Assicurati che i dati siano un array
      const appointmentsArray = Array.isArray(response) ? response : (Array.isArray(response.data) ? response.data : []);
      console.log('Array finale degli appuntamenti:', appointmentsArray);
      
      setBookings(appointmentsArray);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento degli appuntamenti:', err);
      setError('Non è stato possibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSuccess = () => {
    setShowFeedbackForm(false);
    setSelectedBooking(null);
    loadAppointments(); 
  };
  
  // Nuova funzione per aprire il form di feedback
  const handleOpenFeedback = (booking) => {
    setSelectedBooking(booking);
    setShowFeedbackForm(true);
  };

  // Funzione per gestire il click su un appuntamento suggerito
  const handleSuggestedAppointmentClick = (appointment) => {
    // Prepara i dati per la pagina di prenotazione
    const doctor = {
      id: appointment.therapist_id,
      name: appointment.therapist_name
    };
    
    const appointmentData = {
      date: appointment.date,
      time: appointment.time,
      service_name: appointment.service_name,
      price: appointment.price || appointment.total_price,
      service_id: appointment.service_id,
    };
    
    // Naviga alla pagina di riepilogo della prenotazione con i dati dell'appuntamento
    navigate('/patient/bookingsummary', { 
      state: { 
        doctor: doctor, 
        appointmentData: appointmentData
      } 
    });
  };

  // Funzione per il render della foto profilo
  const renderProfileImage = (booking) => {
    if (booking.therapist_profile_picture && !imageErrors[booking.id]) {
      return (
        <img
          src={`${UPLOAD_PREFIX}/storage/${booking.therapist_profile_picture}`}
          alt={booking.therapist_name || `Terapeuta #${booking.therapist_id}`}
          className="w-10 h-10 rounded-full object-cover object-center mr-3"
          onError={() => {
            setImageErrors(prev => ({...prev, [booking.id]: true}));
          }}
        />
      );
    } else {
      // Immagine di fallback
      return <User className="h-5 w-5 text-gray-400 mr-3" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (dateString) => {
    // Estrai solo la parte della data senza orario
    const datePart = dateString.split('T')[0];
    
    // Crea nuove date usando solo la parte della data
    const appointmentDate = new Date(datePart);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return differenceInDays(appointmentDate, today);
  };

  // Filtra gli appuntamenti suggeriti
  const suggestedBookings = bookings
    .filter(booking => booking.status === 'suggested')
    .sort((a, b) => {
      // Ordina per data e ora, prima i più vicini
      const daysRemainingA = getDaysRemaining(a.date);
      const daysRemainingB = getDaysRemaining(b.date);
      
      if (daysRemainingA !== daysRemainingB) {
        return daysRemainingA - daysRemainingB; // Prima i più vicini
      }
      
      // Se sono nello stesso giorno, ordina per orario
      const timeA = new Date(a.time);
      const timeB = new Date(b.time);
      return timeA.getTime() - timeB.getTime(); // Prima quelli con orario più mattutino
    });

  // Dividi gli appuntamenti tra passati e futuri basandoti sui giorni rimanenti, escludi suggeriti
  const upcomingBookings = bookings
    .filter(booking => {
      const daysRemaining = getDaysRemaining(booking.date);
      // Includi solo gli appuntamenti futuri o di oggi, ma non suggeriti
      return daysRemaining >= 0 && booking.status !== 'suggested';
    })
    .sort((a, b) => {
      // Ordina prima per i giorni rimanenti, poi per orario se sono nello stesso giorno
      const daysRemainingA = getDaysRemaining(a.date);
      const daysRemainingB = getDaysRemaining(b.date);
      
      if (daysRemainingA !== daysRemainingB) {
        return daysRemainingA - daysRemainingB; // Prima i più vicini
      }
      
      // Se sono nello stesso giorno, ordina per orario
      const timeA = new Date(a.time);
      const timeB = new Date(b.time);
      return timeA.getTime() - timeB.getTime(); // Prima quelli con orario più mattutino
    });

  const pastBookings = bookings
    .filter(booking => {
      const daysRemaining = getDaysRemaining(booking.date);
      // Includi solo appuntamenti già passati, escludi suggeriti
      return daysRemaining < 0 && booking.status !== 'suggested';
    })
    .sort((a, b) => {
      // Ordina prima i più recenti
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime(); // Ordine decrescente (dal più recente)
    });
    
  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <Sidebar />
      </div>

      {/* Main content area */}
      <div className="flex-1 p-8 bg-[#FAF4EB]">
        <header className="mb-6">
          <h1 className="text-2xl font-bold">Le Mie Prenotazioni</h1>
        </header>

        {!loading && !error && bookings.length === 0 && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            <p className="mb-3">Non hai ancora prenotato appuntamenti.</p>
            <button 
              onClick={() => navigate('/patient/dashboard')} 
              className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200 flex items-center"
            >
              <span className="mr-2">Vai subito qui per scegliere il professionista che ti abbiamo suggerito</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && bookings.length > 0 && (
          <>
            {/* Tab navigation */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                className={`py-2 px-4 mr-2 font-medium rounded-t-lg ${
                  activeTab === 'upcoming' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('upcoming')}
              >
                Prossimi appuntamenti ({upcomingBookings.length})
              </button>
              <button
                className={`py-2 px-4 mr-2 font-medium rounded-t-lg ${
                  activeTab === 'suggested' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('suggested')}
              >
                Appuntamenti suggeriti ({suggestedBookings.length})
              </button>
              <button
                className={`py-2 px-4 font-medium rounded-t-lg ${
                  activeTab === 'past' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('past')}
              >
                Appuntamenti passati ({pastBookings.length})
              </button>
            </div>

            {/* Se non ci sono appuntamenti per il tab selezionato */}
            {activeTab === 'upcoming' && upcomingBookings.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
                <p>Non hai appuntamenti futuri. Perché non ne prenoti uno?</p>
                <button 
                  onClick={() => navigate('/patient/dashboard')} 
                  className="mt-2 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200 flex items-center"
                >
                  <span className="mr-2">Prenota ora</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            )}

            {activeTab === 'past' && pastBookings.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded mb-4">
                <p>Non hai ancora appuntamenti passati.</p>
              </div>
            )}
            
            {activeTab === 'suggested' && suggestedBookings.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded mb-4">
                <p>Non ci sono appuntamenti suggeriti al momento.</p>
              </div>
            )}

            {/* Contenuto in base al tab attivo */}
            {((activeTab === 'upcoming' && upcomingBookings.length > 0) || 
               (activeTab === 'past' && pastBookings.length > 0)) && (
              <div className="mt-4">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data e Ora
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Servizio
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Professionista
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Costo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stato
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(activeTab === 'upcoming' ? upcomingBookings : pastBookings).map((booking) => {
                          const daysRemaining = getDaysRemaining(booking.date);
                          let dayIndicator = '';
                          
                          if (activeTab === 'upcoming') {
                            if (daysRemaining === 0) {
                              dayIndicator = ' (Oggi!)';
                            } else if (daysRemaining === 1) {
                              dayIndicator = ' (Domani)';
                            } else {
                              dayIndicator = ` (tra ${daysRemaining} giorni)`;
                            }
                          } else {
                            // Per gli appuntamenti passati
                            if (daysRemaining === -1) {
                              dayIndicator = ' (Ieri)';
                            } else if (daysRemaining < -1) {
                              dayIndicator = ` (${Math.abs(daysRemaining)} giorni fa)`;
                            }
                          }
                          
                          return (
                            <tr key={booking.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {formatDate(booking.date)}
                                      <span className={`ml-1 text-sm font-medium ${
                                        daysRemaining === 0 ? 'text-red-600 font-bold' : 
                                        daysRemaining > 0 ? 'text-purple-600' : 'text-gray-500'
                                      }`}>
                                        {dayIndicator}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-500">{formatTime(booking.time)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{booking.service_name}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  {renderProfileImage(booking)}
                                  <div className="text-sm text-gray-900">
                                    {booking.therapist_name || `Professionista #${booking.therapist_id}`}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <Euro className="h-5 w-5 text-gray-400 mr-2" />
                                  <div className="text-sm text-gray-900">{booking.total_price}€</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                                  {getStatusLabel(booking.status)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex space-x-2">
                                  {/* Pulsante per la videoconferenza (solo per appuntamenti futuri) */}
                                  {activeTab === 'upcoming' && booking.video_conference_link && (
                                    <button
                                      onClick={() => window.open(booking.video_conference_link, '_blank')}
                                      className="text-green-600 hover:text-green-800"
                                      title={`Partecipa alla videoconferenza (${booking.video_conference_provider || 'Link video'})`}
                                    >
                                      <Video className="h-5 w-5" />
                                    </button>
                                  )}
                                  
                                  {/* Pulsante per il feedback (solo per appuntamenti passati) */}
                                  {activeTab === 'past' && (
                                    <button
                                      onClick={() => handleOpenFeedback(booking)}
                                      className="text-purple-600 hover:text-purple-800"
                                      title="Lascia un feedback"
                                    >
                                      <MessageSquare className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tab appuntamenti suggeriti */}
            {activeTab === 'suggested' && suggestedBookings.length > 0 && (
              <div className="mt-4">
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4">
                  <p className="text-indigo-800 flex items-center mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Un professionista ha suggerito questi appuntamenti per te in base al tuo percorso terapeutico.
                  </p>
                  <p className="text-indigo-700 text-sm">Clicca su un appuntamento per prenotarlo.</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {suggestedBookings.map((appointment) => {
                    const daysRemaining = getDaysRemaining(appointment.date);
                    let dayIndicator = '';
                    
                    if (daysRemaining === 0) {
                      dayIndicator = 'Oggi!';
                    } else if (daysRemaining === 1) {
                      dayIndicator = 'Domani';
                    } else {
                      dayIndicator = `Tra ${daysRemaining} giorni`;
                    }
                    
                    return (
                      <div 
                        key={appointment.id} 
                        className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow duration-200 border-2 border-transparent hover:border-purple-200"
                        onClick={() => handleSuggestedAppointmentClick(appointment)}
                      >
                        <div className="flex items-center mb-3">
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-500 mr-3">
                            <Clock className="h-5 w-5" />
                          </span>
                          <div>
                            <h3 className="font-medium">{appointment.service_name}</h3>
                            <p className="text-sm text-gray-500">con {appointment.therapist_name || `Professionista #${appointment.therapist_id}`}</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm">{formatDate(appointment.date)}</span>
                          </div>
                          <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full font-medium">
                            {dayIndicator}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm">{formatTime(appointment.time)}</span>
                          </div>
                          <div className="flex items-center">
                            <Euro className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="font-medium">{appointment.price || appointment.total_price}€</span>
                          </div>
                        </div>
                        
                        <button className="w-full mt-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors duration-200">
                          Prenota questo appuntamento
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Feedback con il nuovo componente */}
      {showFeedbackForm && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Lascia un feedback</h3>
              <button 
                onClick={() => setShowFeedbackForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              {selectedBooking && `${selectedBooking.service_name} del ${formatDate(selectedBooking.date)}`}
            </p>
            
            <LeaveFeedbackForm 
              therapistId={selectedBooking.therapist_id}
              onSuccess={handleFeedbackSuccess}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientBookingHistoryPage;